package com.downdogapp.client.views

import com.downdogapp.*
import com.downdogapp.FontWeight.SEMIBOLD
import com.downdogapp.JustifyContent.SPACE_BETWEEN
import com.downdogapp.Overflow.SCROLL
import com.downdogapp.Position.ABSOLUTE
import com.downdogapp.Position.RELATIVE
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.Companion.vh
import com.downdogapp.client.*
import com.downdogapp.client.api.Pose
import com.downdogapp.client.controllers.PoseListViewController
import com.downdogapp.client.resources.Image
import com.downdogapp.client.resources.Images
import com.downdogapp.client.resources.image
import com.downdogapp.client.widget.*
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.HTMLInputElement

actual class PoseListView actual constructor(
  private val controller: PoseListViewController
) : TopLevelView(), View {

  private val searchInput: HTMLInputElement
  private val table: TableView
  private val poseConfirmationView: PoseConfirmationView

  override val root = let {
    controllerView {
      bg = if (AppPage.displayingAbovePlayback) {
        background(image = postPracticeBackgroundImage) {
          filter = Filter.blur(px(10))
        }
      } else {
        background(true)
      }
      fg = foreground(!AppPage.displayingAbovePlayback) {
        centeredColumnView {
          position = RELATIVE

          pageHeader(
            title = controller.title,
            titleSize = 18,
            onBackClicked = controller::onBackClicked
          )

          div {
            position = RELATIVE
            width = percent(100)
            height = px(Images.SEARCH_ICON.height)
            marginHorizontal = HORIZONTAL_CONTENT_GUTTER_PX

            searchInput = input {
              type = "text"
              style.height = "100%"
              style.width = "100%"
              style.outline = "none"
              paddingLeft = px(Images.SEARCH_ICON.width)
              placeholder = controller.searchFieldPrompt
              bgColor = Color.grey(0.1)
              borderColor = Color.grey(0.1)
              borderRadius = px(8)
              txtColor = Color.WHITE

              onkeyup = {
                table.refreshContents()
              }
            }

            imageView(Images.SEARCH_ICON) {
              position = ABSOLUTE
              left = px(0)
            }
          }

          table = tableView(::getContents) {
            overflowY = SCROLL
            height = vh(80)
            marginTop = px(20)
          }

          poseConfirmationView = poseConfirmationView {
            position = ABSOLUTE
            bottom = px(20)
            left = percent(50)
            style.transform = "translateX(-50%)"
          }
        }
      }
    }
  }

  actual fun showPoseConfirmation(text: String, image: Image) {
    poseConfirmationView.setText(text)
    poseConfirmationView.setImage(image)
    poseConfirmationView.fadeIn(seconds(0.3))
  }

  actual fun hidePoseConfirmation() {
    poseConfirmationView.fadeOut(seconds(0.3))
  }

  private fun getContents() = TableContents(
    rows = controller.posesMatchingSearch(searchInput.value).map {
      poseListRow(height = 110, pose = it)
    }
  )

  private fun poseListRow(height: Int?,
                          pose: Pose) =
    TableRowInit(height = height?.let { px(it) }, allowSeparator = false) { PoseListRow(pose) }

  inner class PoseListRow(private val pose: Pose) : TableRow() {

    private lateinit var likeButton: HTMLImageElement
    private lateinit var dislikeButton: HTMLImageElement

    override val root =
      createDiv {
        height = percent(100)

        pose.libraryUrlSuffix?.let { libraryUrlSuffix ->
          onClick {
            controller.onPoseClicked(MANIFEST.poseLibraryUrlPrefix + libraryUrlSuffix)
          }
        }

        horizontalFlexBox {
          div {
            marginLeft = HORIZONTAL_CONTENT_GUTTER_PX
            marginRight = px(14)
            imageView(height = 87, width = 155) {
              src = MANIFEST.poseImageUrlPrefix + pose.imageUrlSuffix
            }
          }
          verticalFlexBox(SPACE_BETWEEN) {
            flexGrow = 1.0

            verticalFlexBox {
              label(pose.name, 16, SEMIBOLD) {
                width = percent(100)
              }
            }

            if (pose.isLikeable) {
              horizontalFlexBox {
                likeButton = imageButton(
                  image = Images.THUMBS_UP,
                  clickFn = ::onLikePoseClicked
                ) {
                  height = 36
                  width = 36
                  marginLeft = px(-5)
                }

                dislikeButton = imageButton(
                  image = Images.THUMBS_DOWN,
                  clickFn = ::onDislikePoseClicked
                ) {
                  height = 36
                  width = 36
                  marginLeft = px(4)
                }
              }

              updateSelectedState()
            }
          }

          pose.libraryUrlSuffix?.let {
            imageView(Images.SETTINGS_RIGHT_ARROW) {
              alignSelf = AlignItems.CENTER
            }
          }
        }
      }

    private fun onLikePoseClicked() {
      controller.onLikePoseClicked(pose)
      updateSelectedState()
    }

    private fun onDislikePoseClicked() {
      controller.onDislikePoseClicked(pose)
      updateSelectedState()
    }

    private fun updateSelectedState() {
      likeButton.image =
        if (SequenceSettings.getPoseLiked(pose.poseId) == true) Images.THUMBS_UP_SELECTED else Images.THUMBS_UP
      dislikeButton.image =
        if (SequenceSettings.getPoseLiked(pose.poseId) == false) Images.THUMBS_DOWN_SELECTED else Images.THUMBS_DOWN
    }

    override fun setSeparator(separator: TableSeparator) {
      error("Separators are not supported for Pose List Rows")
    }
  }
}
